import type { StorePrice, StorePurchase } from "@/lib/api/types";

export function formatPrice(price?: StorePrice | null): string | null {
  if (!price) return null;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: price.currency,
      maximumFractionDigits: 2,
    }).format(price.amount);
  } catch {
    return `${price.amount.toFixed(2)} ${price.currency}`;
  }
}

/**
 * A money amount with its own currency, for values that arrive as a number
 * plus an ISO code rather than as a StorePrice.
 *
 * Falls back to the bare number when the currency is unknown — better an
 * unlabelled figure than a figure labelled with someone else's currency.
 */
export function formatMoney(amount: number, currency?: string): string {
  if (!currency) return amount.toLocaleString();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/**
 * Whether this item carries a price it can actually be sold at.
 *
 * Odoo returns `amount: 0.0` for a product whose sales price was never set —
 * it does not omit the `purchase` block — so a truthiness check on `purchase`
 * is not enough to tell "priced at zero" from "unpriced". Rendering that as
 * "TRY 0.00" next to an Add to Cart button advertises a free product and lets
 * a buyer submit a zero-value order, which is the state the live store was in
 * (VARS-121). Treat it as no price: show the quote path instead.
 *
 * Matches the guard already applied to the product JSON-LD `offers` block in
 * shop.$slug.tsx (VARS-084), so schema and UI agree on what counts as priced.
 */
export function hasSellablePrice(price?: StorePrice | null): boolean {
  return Boolean(price) && Number.isFinite(price!.amount) && price!.amount > 0;
}

/**
 * Whether this catalogue item can go into the cart right now.
 *
 * The one definition of that rule: Odoo says it is available, and it carries a
 * price it can be sold at. `StoreCartContext.add()` enforces it, the product
 * card and detail page decide between Add to Cart and the quote CTA with it,
 * and reorder asks it before re-adding a line from a past order. Keeping it in
 * one place is what stops those four from drifting — a card offering a button
 * the cart then silently refuses is worse than either behaviour alone.
 */
export function isPurchasable(item: { purchase?: StorePurchase | null }): boolean {
  return Boolean(item.purchase?.available) && hasSellablePrice(item.purchase);
}
